import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { PM_SERVICE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const API_BASE_URL = `${PM_SERVICE_BASE_URL}/api/v1/tags`;

export default function useTags(page = 0, pageSize = 20) {
  const authToken = sessionStorage.getItem("authToken");
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  // handle axios errors
  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";
    if (err.response) {
      message =
        err.response.data?.message ||
        `Failed to ${action}: ${err.response.statusText}`;
    } else if (err.request) {
      message = "No response from the server. Please check your internet connection.";
    } else {
      message = err.message || "Something went wrong.";
    }
    setError(err);
    toast.error(message, { theme: "colored" });
    console.error(`${action} error:`, err);
  };

  // Fetch all tags
  const fetchTags = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(`${API_BASE_URL}?page=${page}&size=${pageSize}`);
      setTags(res.data.content || res.data);
      setTotalPages(res.data.totalPages || 1);
      setTotalElements(res.data.totalElements || res.data.length || 0);
    } catch (err) {
      handleAxiosErrors(err, "Fetch Tags");
    } finally {
      setLoading(false);
    }
  }, [authToken, page, pageSize]);

  // Create a new tag
  const createTag = useCallback(
    async (tagData) => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.post(API_BASE_URL, tagData);
        setTags((prev) => [...prev, res.data]);
        toast.success("Tag created successfully!", { theme: "colored" });
        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "create tag");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // Fetch one tag by ID
  const fetchTagById = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get(`${API_BASE_URL}/${id}`);
        setSelectedTag(res.data);
        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "fetch tag details");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // Update a tag
  const updateTag = useCallback(
    async (id, updatedData) => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.put(`${API_BASE_URL}/${id}`, updatedData);

        // Update local state
        setTags((prev) =>
          prev.map((tag) => (tag.id === id ? res.data : tag))
        );

        // If currently selected tag matches, update it
        setSelectedTag((prev) => (prev && prev.id === id ? res.data : prev));

        toast.success("Tag updated successfully!", { theme: "colored" });
        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "update tag");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // Delete a tag
  const deleteTag = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);
      try {
        await apiClient.delete(`${API_BASE_URL}/${id}`);

        // Remove deleted tag from local state
        setTags((prev) => prev.filter((tag) => tag.id !== id));
        toast.success("Tag deleted successfully!", { theme: "colored" });
      } catch (err) {
        handleAxiosErrors(err, "delete tag");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // fetch automatically on mount
  useEffect(() => {
    if (authToken) fetchTags();
  }, [authToken, fetchTags]);

  return {
    tags,
    selectedTag,
    loading,
    error,
    fetchTags,
    createTag,
    fetchTagById,
    updateTag,
    deleteTag,
    setSelectedTag,
  };
}